import { useEffect, useRef } from "react";
import { useAuth } from "@/lib/AuthContext";
import { useTheme } from "@/lib/ThemeContext";
import { api } from "@/lib/api";

/**
 * ThemeSyncer — two-way sync between ThemeContext (localStorage-backed)
 * and backend `user_preferences.theme_preset` + `custom_theme`.
 *
 * Flow:
 *   1. On login → fetch prefs → if backend theme differs, apply it
 *      (backend wins on fresh session). If backend has no theme, push local.
 *   2. On theme change → debounce 500 ms → PATCH to backend.
 *   3. On API failure → silently keep local; never flash back to default.
 */
export default function ThemeSyncer() {
  const { user } = useAuth();
  const {
    activeThemeId, setTheme, customOverrides, hasOverrides, updateTokens,
  } = useTheme();

  const hydrated = useRef(false);
  const lastSyncedRef = useRef({ preset: null, custom: null });
  const debounceRef = useRef(null);

  // --- Hydrate from backend on login ---
  useEffect(() => {
    if (!user || hydrated.current) return;
    let cancelled = false;

    api.getPreferences()
      .then((prefs) => {
        if (cancelled) return;
        const backendPreset = prefs?.theme_preset;
        const backendCustom = prefs?.custom_theme;

        // Apply backend preset if it differs from local
        if (backendPreset && backendPreset !== "custom" && backendPreset !== activeThemeId) {
          setTheme(backendPreset);
        }
        // Apply custom overrides if backend has them
        if (backendPreset === "custom" && backendCustom && typeof backendCustom === "object") {
          updateTokens(backendCustom);
        }

        lastSyncedRef.current = { preset: backendPreset || null, custom: backendCustom || null };
        hydrated.current = true;
      })
      .catch(() => {
        // Backend unreachable — keep local state, don't flash default
        hydrated.current = true;
      });

    return () => { cancelled = true; };
  }, [user, activeThemeId, setTheme, updateTokens]);

  // --- Push local theme changes to backend (debounced) ---
  useEffect(() => {
    if (!user || !hydrated.current) return;

    const presetToSend = hasOverrides ? "custom" : activeThemeId;
    const customToSend = hasOverrides ? (customOverrides || {}) : null;

    // Skip if nothing changed since last sync
    const prev = lastSyncedRef.current;
    const customChanged = JSON.stringify(customToSend) !== JSON.stringify(prev.custom);
    if (prev.preset === presetToSend && !customChanged) return;

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const payload = { theme_preset: presetToSend };
      if (customToSend !== null) payload.custom_theme = customToSend;
      api.updatePreferences(payload)
        .then(() => {
          lastSyncedRef.current = { preset: presetToSend, custom: customToSend };
        })
        .catch(() => {
          // Silent fail — theme already applied locally via localStorage
        });
    }, 500);

    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [user, activeThemeId, customOverrides, hasOverrides]);

  // Reset hydration flag on logout so next user re-hydrates
  useEffect(() => {
    if (!user) {
      hydrated.current = false;
      lastSyncedRef.current = { preset: null, custom: null };
    }
  }, [user]);

  return null;
}
